import React, { useEffect, useRef } from "react";
import { CloudOff, Radio, Wifi } from "lucide-react";
import { toast } from "sonner";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { useConnectivity } from "@/hooks/useConnectivity";

/**
 * OfflineBanner — Session 30.35: no longer a banner.
 *
 * Until now this pinned a strip to the top of the screen for as long as
 * the device had no internet — on a router-only network that meant "for
 * the whole shift", and it only appeared on the devices that had actually
 * lost internet (the phone on mobile data never saw it). The owner asked
 * for a brief notification on EVERY dashboard that goes away by itself.
 *
 * So this component renders nothing. It watches connectivity and fires a
 * short toast on each transition:
 *   • internet lost, LAN up   → "Running on LAN — no internet"
 *   • internet lost, no LAN   → "Offline — no internet and no local devices"
 *   • internet back           → "Back online — syncing to the cloud"
 * The persistent indicator is the LAN / Cloud pill in each dashboard header.
 */
const MODE_LAN = "lan";
const MODE_OFFLINE = "offline";
const MODE_ONLINE = "online";
const TOAST_ID = "nfcs-connectivity";

export function OfflineBanner() {
  const { online } = useOnlineStatus();
  const net = useConnectivity();
  const lastModeRef = useRef(null);
  const settleTimerRef = useRef(null);

  const mode = (() => {
    if (net.isNative && net.internet === "down" && net.lanUp) return MODE_LAN;
    if (!online) return MODE_OFFLINE;
    if (net.isNative && net.internet === "down") return net.lanUp ? MODE_LAN : MODE_OFFLINE;
    return MODE_ONLINE;
  })();

  useEffect(() => {
    // Debounce: connectivity flaps for a second or two while Android
    // re-evaluates the network; only announce a state that holds 1.5 s.
    if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
    settleTimerRef.current = setTimeout(() => {
      const prev = lastModeRef.current;
      if (prev === mode) return;
      lastModeRef.current = mode;
      // First evaluation on mount: only speak up if we are NOT online.
      if (prev === null && mode === MODE_ONLINE) return;
      if (mode === MODE_LAN) {
        const n = Number(net.peerCount || 0);
        toast.message("Running on LAN — no internet", {
          id: TOAST_ID,
          icon: <Radio className="h-4 w-4" />,
          description: `Connected to ${n} other device${n === 1 ? "" : "s"} on this Wi-Fi. Orders keep flowing; cloud sync resumes automatically.`,
          duration: 6000,
        });
      } else if (mode === MODE_OFFLINE) {
        toast.warning("Offline — no internet and no local devices", {
          id: TOAST_ID,
          icon: <CloudOff className="h-4 w-4" />,
          description: "Everything you do is saved here and syncs automatically when a connection returns.",
          duration: 8000,
        });
      } else if (prev !== null) {
        toast.success("Back online", {
          id: TOAST_ID,
          icon: <Wifi className="h-4 w-4" />,
          description: "Syncing to the cloud.",
          duration: 4000,
        });
      }
    }, 1500);
    return () => { if (settleTimerRef.current) clearTimeout(settleTimerRef.current); };
  }, [mode, net.peerCount]);

  return null;
}

export default OfflineBanner;
